import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styled from 'styled-components';
import vars from '@exo/narrative-tracker-variables';
import { placeholder, smallCaps } from '@exo/style-mixins';
import Textarea from 'react-textarea-autosize';
import { Pencil, Search } from '@exo/icons-lite';
import { higherContrast } from '@exo/helpers';

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var stripPX = function stripPX(numberPX) {
	var stringNumber = numberPX.replace(/(px|%)$/, "");
	return parseInt(stringNumber, 10);
};

// ------ Styled components ------
// reused snippets
var baseInputStyle = "\n\theight: 44px;\n\twidth: 100%;\n\t" + vars.mainfont + ";\n\tborder: none;\n\tresize: none;\n\tbox-sizing: border-box;\n\t" + placeholder("\n\t\t" + vars.mainfontBold + "\n\t\tletter-spacing: 0.0625em;\n\t") + "\n\t&:focus {\n\t\toutline: none;\n\t}\n\t&:disabled {\n\t\topacity: 0.5;\n\t}\n";

// Components
var InputRoot = styled.span.withConfig({
	displayName: "Input__InputRoot",
	componentId: "sc-6d0f59-0"
})(["position:relative;display:block;"]);
var Label = styled.span.withConfig({
	displayName: "Input__Label",
	componentId: "sc-6d0f59-1"
})(["display:block;padding-bottom:4px;", " font-size:14px;"], smallCaps(vars.mainfontBold));
var AcceptButton = styled.span.withConfig({
	displayName: "Input__AcceptButton",
	componentId: "sc-6d0f59-2"
})(["", " display:block;position:absolute;left:", ";right:", ";height:20px;top:calc(50% - 10px);min-width:12px;", " font-size:12px;border-radius:4px;color:", ";background-color:", ";text-align:center;cursor:pointer;z-index:-1;box-sizing:border-box;opacity:0;transition:z-index 1ms linear 150ms,background-color 350ms ease-out 50ms,opacity 350ms ease-out 50ms;&:hover{background-color:", ";}"], smallCaps(vars.mainfontBold), function (props) {
	return props.rtlMode ? "5px" : "auto";
}, function (props) {
	return props.rtlMode ? "auto" : "5px";
}, function (props) {
	return _typeof(props.acceptBtnText) === "object" // is accept button using an icon?
	? "width: 20px; padding: 4px 5px;" : "padding: 2px 7px;";
}, function (props) {
	return props.textColor;
}, function (props) {
	return props.theColor;
}, function (props) {
	return props.hoverColor;
});
// Designed to have the whole button be the icon
var ClearButton = styled.span.withConfig({
	displayName: "Input__ClearButton",
	componentId: "sc-6d0f59-3"
})(["display:block;position:absolute;left:", ";right:", ";top:50%;margin-top:-7px;height:14px;width:14px;border-radius:50%;text-align:center;cursor:pointer;z-index:1;pointer-events:all;svg{opacity:0.7;}&:hover{svg{opacity:1;}}&:focus{", "}"], function (props) {
	return props.rtlMode ? "7px" : "auto";
}, function (props) {
	return props.rtlMode ? "auto" : "7px";
}, function (props) {
	return props.acceptBtn ? "padding-right: 50px;" : "";
});
var InfoButton = styled.span.withConfig({
	displayName: "Input__InfoButton",
	componentId: "sc-6d0f59-4"
})(["display:block;position:absolute;left:", ";right:", ";top:0;bottom:0;text-align:center;cursor:pointer;z-index:1;pointer-events:all;svg{opacity:0.7;}&:hover{svg{opacity:1;}}&:focus{", "}"], function (props) {
	return props.rtlMode ? "7px" : "auto";
}, function (props) {
	return props.rtlMode ? "auto" : "7px";
}, function (props) {
	return props.acceptBtn ? "padding-right: 50px;" : "";
});
var InputComponent = styled.input.withConfig({
	displayName: "Input__InputComponent",
	componentId: "sc-6d0f59-5"
})(["", " background-color:", ";border:1px solid ", ";border-radius:", "px;color:", ";direction:", ";font-size:", "px;height:", "px;padding-left:", "px;padding-right:", "px;text-align:", ";transition:", ";unicode-bidi:", ";&:hover{color:", ";background-color:", ";border-color:", ";}&:focus{outline:none;background-color:", ";border:1px solid ", ";box-shadow:0px 0px 20px 0px ", ";}&:focus ~ ", "{opacity:1;z-index:2;transition:background-color 350ms ease-out 50ms,opacity 350ms ease-out 50ms;}&:focus ~ ", "{opacity:", ";pointer-events:", ";}::-webkit-input-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}::-moz-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}:-ms-input-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}:-moz-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}"], baseInputStyle, function (props) {
	return props.backgroundColor;
}, function (props) {
	return props.borderColor;
}, function (props) {
	return props.bdrRadius;
}, function (props) {
	return props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.fontSize;
}, function (props) {
	return props.theHeight;
}, function (props) {
	return props.leftPadding || props.sidePadding;
}, function (props) {
	return props.rightPadding || props.sidePadding;
}, function (props) {
	return props.textAlign;
}, function (props) {
	return props.transition;
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.textHoverColor;
}, function (props) {
	return props.backgroundHoverColor || props.backgroundColor;
}, function (props) {
	return props.borderHoverColor || props.borderColor;
}, function (props) {
	return props.backgroundFocusColor || props.backgroundColor;
}, function (props) {
	return props.borderFocusColor || props.borderColor;
}, function (props) {
	return props.focusGlowColor;
}, AcceptButton, ClearButton, function (props) {
	return props.acceptBtn ? 0 : 1;
}, function (props) {
	return props.acceptBtn ? "none" : "all";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
});
var StyledTextarea = styled.div.withConfig({
	displayName: "Input__StyledTextarea",
	componentId: "sc-6d0f59-6"
})(["textarea{", " background-color:", ";border:1px solid ", ";border-radius:", "px;box-sizing:", ";color:", ";direction:", ";font-size:", "px;line-height:150%;max-height:", ";padding-top:", "px;padding-bottom:", "px;padding-left:", "px;padding-right:", "px;text-align:", ";transition:", ";overflow:", ";width:", ";unicode-bidi:", ";&:hover{color:", ";background-color:", ";border-color:", ";}&:focus{outline:none;background-color:", ";border:1px solid ", ";box-shadow:0px 0px 20px 0px ", ";}&:focus ~ ", "{opacity:1;pointer-events:all;}&:focus ~ ", "{opacity:", ";pointer-events:", ";}&::-webkit-input-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}&::-moz-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}&:-ms-input-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}&:-moz-placeholder{font-style:italic;font-weight:200;color:", ";direction:", ";unicode-bidi:", ";}}"], baseInputStyle, function (props) {
	return props.backgroundColor;
}, function (props) {
	return props.borderColor;
}, function (props) {
	return props.bdrRadius;
}, function (props) {
	return props.expandOnFocus ? "content-box" : "border-box";
}, function (props) {
	return props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.fontSize;
}, function (props) {
	return props.maxHeight;
}, function (props) {
	return props.verticalPadding;
}, function (props) {
	return props.verticalPadding;
}, function (props) {
	return props.leftPadding;
}, function (props) {
	return props.rightPadding;
}, function (props) {
	return props.textAlign;
}, function (props) {
	return props.transition;
}, function (props) {
	return props.hideOverflow ? "hidden" : "auto";
}, function (props) {
	return props.expandOnFocus ? "calc(100% - " + (props.leftPadding + props.rightPadding) + "px)" : "100%";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.textHoverColor;
}, function (props) {
	return props.backgroundHoverColor || props.backgroundColor;
}, function (props) {
	return props.borderHoverColor || props.borderColor;
}, function (props) {
	return props.backgroundFocusColor || props.backgroundColor;
}, function (props) {
	return props.borderFocusColor || props.borderColor;
}, function (props) {
	return props.focusGlowColor;
}, AcceptButton, ClearButton, function (props) {
	return props.acceptBtn ? 0 : 1;
}, function (props) {
	return props.acceptBtn ? "none" : "all";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
}, function (props) {
	return props.placeholderColor || props.textColor;
}, function (props) {
	return props.rtl ? "rtl" : "ltr";
}, function (props) {
	return props.rtl ? "bidi-override" : "normal";
});
var EditButton = styled.span.withConfig({
	displayName: "Input__EditButton",
	componentId: "sc-6d0f59-7"
})(["display:block;position:absolute;left:-10px;top:50%;margin-top:-6px;height:12px;width:12px;background-repeat:no-repeat;background-size:cover;cursor:pointer;"]);
var SearchIcon = styled.span.withConfig({
	displayName: "Input__SearchIcon",
	componentId: "sc-6d0f59-8"
})(["display:block;position:absolute;left:", ";right:", ";top:50%;margin-top:-", "px;height:", "px;width:", "px;background-repeat:no-repeat;background-size:cover;pointer-events:none;"], function (props) {
	return props.alignRight ? "auto" : "calc(" + props.room + "px / 2 - " + (props.searchIconHeight * 0.75 / 2 - 2) + "px)";
}, function (props) {
	return props.alignRight ? "calc(" + props.room + "px / 2 - " + (props.searchIconHeight * 0.75 / 2 - 2) + "px)" : "auto";
}, function (props) {
	return props.searchIconHeight / 2 + (props.misalignForTextareaBug ? 2 : 0);
}, function (props) {
	return props.searchIconHeight;
}, function (props) {
	return props.searchIconHeight * 0.75;
});
var Note = styled.p.withConfig({
	displayName: "Input__Note",
	componentId: "sc-6d0f59-9"
})(["position:absolute;", " margin:6px 0 0 5px;font-size:12px;color:", ";"], vars.mainfont, vars.grey_2);

var Input = function (_React$Component) {
	inherits(Input, _React$Component);

	function Input(props) {
		classCallCheck(this, Input);

		var _this = possibleConstructorReturn(this, (Input.__proto__ || Object.getPrototypeOf(Input)).call(this, props));

		_this.state = {
			expand: false
		};

		_this.acceptBtn = _this.acceptBtn.bind(_this);
		_this.editFocus = _this.editFocus.bind(_this);
		_this.expandOnFocus = _this.expandOnFocus.bind(_this);
		_this.handleChange = _this.handleChange.bind(_this);
		_this.keyDown = _this.keyDown.bind(_this);
		_this.selectAllOnFocus = _this.selectAllOnFocus.bind(_this);
		_this.shrinkOnBlur = _this.shrinkOnBlur.bind(_this);
		return _this;
	}

	createClass(Input, [{
		key: "componentDidUpdate",
		value: function componentDidUpdate(prevProps) {
			if (!prevProps.focus && this.props.focus) {
				this.textInput.focus();
			} else if (prevProps.focus && !this.props.focus) {
				this.textInput.blur();
			}
		}
	}, {
		key: "acceptBtn",
		value: function acceptBtn() {
			var props = this.props;

			if (props.acceptBtnFunction) {
				props.acceptBtnFunction(props.value);
			} else {
				this.textInput.blur();
			}
		}
	}, {
		key: "shrinkOnBlur",
		value: function shrinkOnBlur() {
			this.setState({
				expand: false
			});
		}
	}, {
		key: "expandOnFocus",
		value: function expandOnFocus() {
			this.setState({
				expand: true
			});
		}
	}, {
		key: "handleChange",
		value: function handleChange(event) {
			var onChange = this.props.onChange;

			onChange(event.target.value);
		}
	}, {
		key: "keyDown",
		value: function keyDown(event) {
			// newLineOnEnter blocks acceptBtnFuntion & blur on enter, along with allowing the new line
			var _props = this.props,
			    acceptBtnFunction = _props.acceptBtnFunction,
			    newLineOnEnter = _props.newLineOnEnter,
			    value = _props.value;

			if (event.nativeEvent && !event.nativeEvent.isComposing) {

				var keyCode = event.key || event.keyCode;
				var clickedEnter = keyCode === "Enter" || keyCode === 13;

				if (clickedEnter && !event.shiftKey && !newLineOnEnter) {
					event.preventDefault(); // disable new line on enter

					if (acceptBtnFunction) {
						acceptBtnFunction(value);
					} else {
						this.textInput.blur();
					}
				}
			}
		}
	}, {
		key: "editFocus",
		value: function editFocus() {
			if (!this.props.textarea) {
				this.textInput.focus();
			}
		}
	}, {
		key: "selectAllOnFocus",
		value: function selectAllOnFocus() {
			var _this2 = this;

			setTimeout(function () {
				_this2.textInput.select();
			}, 1);
		}
	}, {
		key: "render",
		value: function render() {
			var _this3 = this;

			var props = this.props,
			    state = this.state;
			var dir = props.dir,
			    fontSize = props.fontSize,
			    textAlign = props.textAlign,
			    acceptBtnColor = props.acceptBtnColor,
			    acceptBtnHoverColor = props.acceptBtnHoverColor,
			    autofillBackgroundColorOverride = props.autofillBackgroundColorOverride,
			    backgroundColor = props.backgroundColor,
			    backgroundHoverColor = props.backgroundHoverColor,
			    backgroundFocusColor = props.backgroundFocusColor,
			    borderColor = props.borderColor,
			    borderHoverColor = props.borderHoverColor,
			    borderFocusColor = props.borderFocusColor,
			    borderRadius = props.borderRadius,
			    browserType = props.browserType,
			    focusGlowColor = props.focusGlowColor,
			    leftPadding = props.leftPadding,
			    placeholderColor = props.placeholderColor,
			    rightPadding = props.rightPadding,
			    searchIcon = props.searchIcon,
			    searchIconColor = props.searchIconColor,
			    searchIconHeight = props.searchIconHeight,
			    textColor = props.textColor,
			    textHoverColor = props.textHoverColor,
			    transition = props.transition;


			if (props.clearBtn && !props.clearBtnFunction || props.clearBtn && !props.clearBtnIcon) {
				console.error("Input.jsx - if prop clearButton is true, both clearBtnFunction and clearBtnIcon are required", props.clearBtn, props.clearBtnFunction, props.clearBtnIcon);
			}

			var height = (typeof props.height === "string" ? stripPX(props.height) : props.height) - (props.expandOnFocus ? 9 : 0);

			var inputClass = classnames("input", props.theme, { acceptBtn: props.acceptBtn });
			var acceptClass = classnames("input__accept-btn", {
				icon: _typeof(props.acceptBtnText) === "object"
			});

			var acceptBtnText = props.acceptBtnText ? props.acceptBtnText : "ok";

			var rtl = dir === "rtl";
			var searchIconSpace = searchIconHeight * 0.75 + 14;

			var useTextarea = props.textarea || props.fakeInput || props.expandOnFocus;
			var lookLikeAnInput = props.fakeInput || props.expandOnFocus && !state.expand;
			var adjustedRightPadding = rightPadding + (props.infoBtn ? 32 : 0) + (props.clearBtn ? 27 : 0);

			return React.createElement(
				InputRoot,
				{ className: inputClass },
				props.label && React.createElement(
					Label,
					{ className: "input__label" },
					props.label
				),
				searchIcon && React.createElement(
					SearchIcon,
					{
						className: "input__searchIcon",
						searchIconHeight: searchIconHeight,
						alignRight: rtl,
						room: rtl ? rightPadding || searchIconSpace : leftPadding || searchIconSpace,
						misalignForTextareaBug: useTextarea
					},
					React.createElement(Search, { fill: searchIconColor })
				),
				props.edit && React.createElement(
					EditButton,
					{ className: "input__edit", onClick: this.editFocus },
					React.createElement(Pencil, { fill: props.editFill ? props.editFill : "#000" })
				),
				useTextarea ? React.createElement(
					StyledTextarea,
					{
						acceptBtn: props.acceptBtn,
						acceptBtnColor: acceptBtnColor,
						backgroundColor: backgroundColor,
						backgroundHoverColor: backgroundHoverColor,
						backgroundFocusColor: backgroundFocusColor,
						borderColor: borderColor,
						borderHoverColor: borderHoverColor,
						borderFocusColor: borderFocusColor,
						bdrRadius: borderRadius,
						expandOnFocus: props.expandOnFocus,
						disabled: props.disabled,
						focusGlowColor: focusGlowColor,
						fontSize: fontSize,
						hideOverflow: props.fakeInput || props.expandOnFocus,
						leftPadding: leftPadding || (searchIcon ? searchIconSpace : 10),
						maxHeight: lookLikeAnInput ? height + "px" : "none",
						placeholderColor: placeholderColor,
						rightPadding: rightPadding || (searchIcon ? searchIconSpace : 10),
						sidePadding: searchIcon ? searchIconSpace : 10,
						readOnly: props.readonly,
						rtl: rtl,
						textAlign: textAlign,
						textColor: textColor,
						textHoverColor: textHoverColor,
						transition: transition,
						verticalPadding: props.fakeInput || props.expandOnFocus ? (height - fontSize * 1.5) / 2 : 13
					},
					React.createElement(Textarea, {
						type: props.type,
						inputRef: function inputRef(textarea) {
							_this3.textInput = textarea;
						},
						maxRows: lookLikeAnInput ? 1 : 99999,
						id: props.id,
						dir: dir,
						value: props.value,
						minRows: props.minRows,
						onBlur: function onBlur(event) {
							props.onBlur && props.onBlur(event);
							props.expandOnFocus && _this3.shrinkOnBlur();
						},
						onChange: function onChange(event) {
							return _this3.handleChange(event);
						},
						onFocus: function onFocus(event) {
							props.onFocus && props.onFocus(event);
							props.selectAllOnFocus && _this3.selectAllOnFocus();
							props.expandOnFocus && _this3.expandOnFocus();
						},
						onKeyDown: function onKeyDown(event) {
							return _this3.keyDown(event, _this3);
						},
						placeholder: props.placeholder
					})
				) : React.createElement(InputComponent, {
					type: "" + (props.type ? props.type : "text"),
					ref: function ref(input) {
						_this3.textInput = input;
					},
					id: props.id,
					value: props.value,
					onBlur: function onBlur() {
						return props.onBlur && props.onBlur();
					},
					onChange: this.handleChange,
					onFocus: function onFocus() {
						props.onFocus && props.onFocus();
						props.selectAllOnFocus && _this3.selectAllOnFocus();
					},
					onKeyDown: function onKeyDown(event) {
						return _this3.keyDown(event, _this3);
					},
					placeholder: props.placeholder,
					theHeight: height,
					disabled: props.disabled,
					sidePadding: searchIcon ? searchIconHeight * 0.75 + 14 : 10,
					leftPadding: leftPadding,
					rightPadding: adjustedRightPadding,
					autoComplete: props.noAutocomplete ? "off" : "on",
					"data-lpignore": props.notPassword,
					name: props.noAutocomplete ? props.id + "-no_complete" : undefined,
					acceptBtn: props.acceptBtn,
					readOnly: props.readonly,
					dir: dir,
					rtl: rtl,
					fontSize: fontSize,
					textAlign: textAlign,
					acceptBtnColor: acceptBtnColor,
					autofillBackgroundColorOverride: autofillBackgroundColorOverride,
					backgroundColor: backgroundColor,
					backgroundHoverColor: backgroundHoverColor,
					backgroundFocusColor: backgroundFocusColor,
					borderColor: borderColor,
					borderHoverColor: borderHoverColor,
					borderFocusColor: borderFocusColor,
					bdrRadius: borderRadius,
					focusGlowColor: focusGlowColor,
					placeholderColor: placeholderColor,
					textColor: textColor,
					textHoverColor: textHoverColor,
					transition: transition
				}),
				props.acceptBtn && props.value && React.createElement(
					AcceptButton,
					{
						className: acceptClass,
						onClick: this.acceptBtn,
						acceptBtnText: props.acceptBtnText,
						textColor: props.acceptBtnTextColor || higherContrast({ color1: "#ffffff", color2: "#000000", background: acceptBtnColor }),
						theColor: acceptBtnColor,
						hoverColor: acceptBtnHoverColor,
						rtlMode: rtl
					},
					(typeof acceptBtnText === "undefined" ? "undefined" : _typeof(acceptBtnText)) === "object" ? React.cloneElement(acceptBtnText) : acceptBtnText
				),
				props.clearBtn && props.value && React.createElement(
					ClearButton,
					{
						className: "input__clearBtn",
						onClick: function onClick() {
							return props.clearBtnFunction();
						},
						acceptBtn: props.acceptBtn,
						rtlMode: rtl
					},
					React.cloneElement(props.clearBtnIcon)
				),
				props.infoBtn && React.createElement(
					InfoButton,
					{
						onClick: function onClick() {
							return props.infoBtnFunction();
						},
						acceptBtn: props.acceptBtn,
						rtlMode: rtl
					},
					React.cloneElement(props.infoBtnIcon)
				),
				props.note && React.createElement(
					Note,
					{ className: "input__note" },
					props.note()
				)
			);
		}
	}]);
	return Input;
}(React.Component);

Input.defaultProps = {
	borderRadius: 4,
	expandOnFocus: false,
	dir: "auto",
	fakeInput: false,
	focus: false,
	fontSize: 14,
	height: 44,
	infoButton: false,
	infoBtnFunction: function infoBtnFunction() {},
	infoBtnIcon: null,
	type: "text",
	leftPadding: 0,
	newLineOnEnter: false,
	noAutocomplete: false,
	notPassword: false,
	onBlur: false,
	onFocus: false,
	readonly: false,
	rightPadding: 0,
	searchIcon: false,
	selectAllOnFocus: false,
	textAlign: "left",
	transition: "opacity 350ms ease-out 50ms, color 350ms ease-out 50ms, background-color 350ms ease-out 50ms",

	// ---- colors ----
	acceptBtnColor: "#00bff3",
	acceptBtnHoverColor: "#0093bb",
	backgroundColor: "#ffffff",
	backgroundHoverColor: false,
	backgroundFocusColor: false,
	borderColor: "transparent",
	borderHoverColor: false,
	borderFocusColor: false,
	focusGlowColor: "transparent",
	placeholderColor: false,
	searchIconColor: "#333333",
	searchIconHeight: 16,
	textColor: "#333333",
	textHoverColor: false
};

Input.propTypes = {
	acceptBtn: PropTypes.bool, // default is false
	// default is blur, otherwise fires passed function. Also works on enter with no button
	acceptBtnFunction: PropTypes.func,
	acceptBtnText: PropTypes.oneOfType([PropTypes.string, // default is 'ok'. Should be 2-3 letters.
	PropTypes.object] // IconComponent
	),
	acceptBtnTextColor: PropTypes.string,
	borderRadius: PropTypes.number,
	browserType: PropTypes.string,
	// Shows a button when value is not "" and when the input is not focused
	clearBtn: PropTypes.bool, // default is false
	// clearBtnFunction and clearBtnText are both required if clearBtn is true
	clearBtnFunction: PropTypes.func,
	clearBtnIcon: PropTypes.object, // IconComponent
	dir: PropTypes.string,
	disabled: PropTypes.bool,
	edit: PropTypes.bool, // default is false. Shows an edit pencil icon
	editFill: PropTypes.string, // default is #000. Doesn't matter if edit: false
	expandOnFocus: PropTypes.bool,
	fakeInput: PropTypes.bool,
	focus: PropTypes.bool, // can set focus by passing a prop
	fontSize: PropTypes.number,
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	id: PropTypes.string, // for testing, or if using an external label
	infoButton: PropTypes.bool, // default is false
	infoBtnFunction: PropTypes.func,
	infoBtnIcon: PropTypes.object, // IconComponent
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	leftPadding: PropTypes.number,
	minRows: PropTypes.number, // for Textarea only
	newLineOnEnter: PropTypes.bool,
	noAutocomplete: PropTypes.bool,
	note: PropTypes.func, // appears below input. Expects a React component
	notPassword: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
	onBlur: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	onFocus: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	placeholder: PropTypes.string,
	readonly: PropTypes.bool,
	rightPadding: PropTypes.number,
	searchIcon: PropTypes.bool,
	selectAllOnFocus: PropTypes.bool,
	textarea: PropTypes.bool, // true turns input into a textarea
	textAlign: PropTypes.string,
	// "dark", 'light', 'inline'. Themes show a box, none is inline. Add ' search' to any
	// "noBackgroundShift" prevents focus from changing the background color (important when using a clear button without an OK button)
	theme: PropTypes.string,
	transition: PropTypes.string,
	type: PropTypes.string, // input type. Defaults to 'text'
	value: PropTypes.string.isRequired,

	// ---- colors ----
	acceptBtnColor: PropTypes.string,
	acceptBtnHoverColor: PropTypes.string,
	backgroundColor: PropTypes.string,
	backgroundHoverColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	backgroundFocusColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	borderColor: PropTypes.string,
	borderHoverColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	borderFocusColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	focusGlowColor: PropTypes.string,
	placeholderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	searchIconColor: PropTypes.string,
	searchIconHeight: PropTypes.number,
	textColor: PropTypes.string,
	textHoverColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

export default Input;
//# sourceMappingURL=index.es.js.map
